/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { JSX } from 'preact';
import { VisStatusMessage } from '#PRIVATE_VisStatusMessage/VisStatusMessage';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { Size } from '#utils/UNSAFE_size';

type Props = {
  /**
   * The width of the component. The default value for this is theme driven.
   */
  width?: Size;

  /**
   * The height of the component. The default value for this is theme driven.
   */
  height?: Size;

  /**
   * The aria label of the component.
   */
  'aria-label'?: UnpackSignals<JSX.AriaAttributes>['aria-label'];

  /**
   * One or more ids (separated by spaces) of elements that describe the component.
   */
  'aria-describedby'?: UnpackSignals<JSX.AriaAttributes>['aria-describedby'];

  /**
   * One or more ids (separated by spaces) of elements that label the component.
   */
  'aria-labelledby'?: UnpackSignals<JSX.AriaAttributes>['aria-labelledby'];
};

/**
 * The VisNoDataMessage component can be used as fallback content when there is no data to display in a visualization.
 */
export function VisNoDataMessage(props: Props) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const noDataMsg = translations.vis_noData();
  return (
    <VisStatusMessage
      text={noDataMsg}
      width={props.width}
      height={props.height}
      aria-label={props['aria-label']}
      aria-describedby={props['aria-describedby']}
      aria-labelledby={props['aria-labelledby']}
    />
  );
}
